import React, { useMemo } from "react";
import { Modal, Tooltip } from "antd";
import {
  buildCardRulesModalSections,
  formatCardRuleFee,
  formatRatePercent,
  resolveCardInfoHyperPayRechargeRate,
  resolveCardInfoPlatformRechargeRate,
  resolveCardInfoTopUpFeeRate,
  resolveCardInfoTransactionFees,
} from "@/utils/spikexCard";
import modalStyles from "./spikexModal.module.less";
import styles from "./index.module.less";

function InfoIcon() {
  return (
    <svg className={modalStyles.cardRulesInfoIcon} viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.2" />
      <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="8" cy="5.1" r="0.7" fill="currentColor" />
    </svg>
  );
}

function CardRulesSection({ title, children, className = "" }) {
  return (
    <section className={`${modalStyles.cardRulesSection} ${className}`.trim()}>
      <h3 className={modalStyles.cardRulesSectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

function DepositFeeInfoTip({ t, tip, spikexPc }) {
  return (
    <Tooltip
      title={tip}
      trigger={["click", "hover"]}
      placement="top"
      align={{ offset: [0, -6] }}
      mouseEnterDelay={0.05}
      destroyTooltipOnHide
      getPopupContainer={() => document.body}
      overlayClassName={spikexPc ? "spikexCardRulesTipOverlay" : modalStyles.cardRulesInfoTooltip}
      color={spikexPc ? undefined : "rgba(0, 0, 0, 0.85)"}
      zIndex={10060}
    >
      <span
        className={modalStyles.cardRulesInfoTrigger}
        role="button"
        tabIndex={0}
        aria-label={tip}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
      >
        <button type="button" className={modalStyles.cardRulesInfoBtn} tabIndex={-1} aria-hidden>
          <InfoIcon />
        </button>
      </span>
    </Tooltip>
  );
}

export default function CardRulesModal({ t, open, onClose, cardInfo, spikexPc = false }) {
  const sections = useMemo(() => buildCardRulesModalSections(cardInfo), [cardInfo]);

  const fees = useMemo(() => {
    const raw = resolveCardInfoTransactionFees(cardInfo);
    return {
      perTransaction: formatCardRuleFee(raw.perTransaction),
      perFailed: formatCardRuleFee(raw.perFailed),
      perAuth: formatCardRuleFee(raw.perAuth),
    };
  }, [cardInfo]);

  const rechargeRates = useMemo(() => {
    const platformRate = formatRatePercent(resolveCardInfoPlatformRechargeRate(cardInfo)) ?? "—";
    const hyperPayRate = formatRatePercent(resolveCardInfoHyperPayRechargeRate(cardInfo)) ?? "—";
    const totalRate = formatRatePercent(resolveCardInfoTopUpFeeRate(cardInfo)) ?? "—";
    return { platformRate, hyperPayRate, totalRate };
  }, [cardInfo]);

  const rechargeInfoTip = t("spikexCard.cardRulesModalRechargeInfo", {
    platformRate: rechargeRates.platformRate,
    hyperPayRate: rechargeRates.hyperPayRate,
  });

  const ruleRows = [
    {
      key: "success",
      labelKey: "spikexCard.cardRulesModalSuccess",
      fee: fees.perTransaction,
    },
    {
      key: "failed",
      labelKey: "spikexCard.cardRulesModalFailed",
      fee: fees.perFailed,
    },
    {
      key: "verification",
      labelKey: "spikexCard.cardRulesModalVerification",
      fee: fees.perAuth,
    },
  ];

  return (
    <Modal
      className={`${styles.cardRulesModal} ${modalStyles.modalRoot}`}
      wrapClassName={modalStyles.modalWrap}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      width={spikexPc ? 560 : 343}
      maskClosable
      destroyOnClose
      title={null}
      zIndex={1000}
    >
      <div className={modalStyles.cardRulesShell}>
        <header className={modalStyles.cardRulesHeader}>
          <h2 className={modalStyles.cardRulesTitle}>{t("spikexCard.cardRules")}</h2>
        </header>

        <div className={modalStyles.cardRulesScroll}>
          <CardRulesSection title={t("spikexCard.cardRulesModalTitle")}>
            <div className={modalStyles.cardRulesRechargeFeeRow}>
              <p className={modalStyles.cardRulesRechargeFee}>
                {t("spikexCard.cardRulesModalRechargeFee", { rate: rechargeRates.totalRate })}
              </p>
              <DepositFeeInfoTip t={t} tip={rechargeInfoTip} spikexPc={spikexPc} />
            </div>
            <p className={modalStyles.cardRulesIntro}>
              {t("spikexCard.cardRulesModalIntro", { fee: fees.perTransaction })}
            </p>
            <ul className={modalStyles.cardRulesList}>
              {ruleRows.map((row) => (
                <li key={row.key} className={modalStyles.cardRulesListItem}>
                  <span className={modalStyles.cardRulesListLabel}>
                    {t(row.labelKey, { fee: row.fee })}
                  </span>
                </li>
              ))}
            </ul>
            <p className={modalStyles.cardRulesNote}>{t("spikexCard.cardRulesModalNote")}</p>
            <p className={modalStyles.cardRulesPrinciple}>
              {t("spikexCard.cardRulesModalPrinciple", { fee: fees.perTransaction })}
            </p>
          </CardRulesSection>

          <CardRulesSection title={t("spikexCard.feeTransactionRuleTitle")}>
            <dl className={modalStyles.cardRulesKvList}>
              {sections.transactionCaps.map((row) => (
                <div key={row.key} className={modalStyles.cardRulesKvRow}>
                  <dt className={modalStyles.cardRulesKvLabel}>{t(row.labelKey)}</dt>
                  <dd className={modalStyles.cardRulesKvValue}>
                    {row.valueKey ? t(row.valueKey) : row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </CardRulesSection>

          <div className={modalStyles.cardRulesFeatureTags}>
            {sections.features.map((labelKey) => (
              <span key={labelKey} className={modalStyles.cardRulesFeatureTag}>
                {t(labelKey)}
              </span>
            ))}
          </div>
        </div>

        <footer className={modalStyles.cardRulesFooter}>
          <button type="button" className={modalStyles.cardRulesConfirmBtn} onClick={onClose}>
            {t("spikexCard.cardRulesModalConfirm")}
          </button>
        </footer>
      </div>
    </Modal>
  );
}
